import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, InputBase, useTheme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import { NAV_SECTIONS } from "navigation";

type PaletteItem = {
  section: string;
  name: string;
  to: string;
  icon: React.ReactNode;
};

const ALL_ITEMS: PaletteItem[] = NAV_SECTIONS.flatMap((section) =>
  section.items.map((r) => {
    const Icon = r.icon;
    return {
      section: section.label,
      name: r.name,
      to: r.layout + r.path,
      icon: <Icon fontSize="small" />,
    };
  }),
);

export const useCommandPalette = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return { open, setOpen };
};

const CommandPalette = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { palette } = useTheme();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ALL_ITEMS;
    return ALL_ITEMS.filter((item) => item.name.toLowerCase().includes(q));
  }, [query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setHighlighted(0);
    }
  }, [open]);

  useEffect(() => setHighlighted(0), [query]);

  const go = (item: PaletteItem) => {
    onClose();
    navigate(item.to);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[highlighted]) go(results[highlighted]);
    }
  };

  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${highlighted}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [highlighted]);

  let lastSection = "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{
        // above every dialog in the app (some legacy hosts use 9100/9999)
        // so ⌘K is never an invisible focus-stealing overlay
        zIndex: 10500,
        "& .MuiDialog-container": { alignItems: "flex-start", pt: "12vh" },
        "& .MuiDialog-paper": {
          backgroundColor: palette.paper.elevation3,
          border: `1px solid ${palette.white[10]}`,
          borderRadius: "20px",
        },
      }}
    >
      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2.5,
          py: 1.75,
          borderBottom: `1px solid ${palette.white[8]}`,
        }}
      >
        <SearchIcon sx={{ color: palette.text.disabled }} />
        <InputBase
          autoFocus
          fullWidth
          placeholder="Jump to…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          inputProps={{
            role: "combobox",
            "aria-expanded": true,
            "aria-controls": "command-palette-listbox",
            "aria-activedescendant":
              results[highlighted] !== undefined
                ? `command-palette-option-${highlighted}`
                : undefined,
          }}
          sx={{
            fontFamily: "Gilroy-Medium",
            fontSize: "16px",
            color: palette.text.primary,
          }}
        />
        <DSBox
          sx={{
            px: 1,
            py: 0.25,
            borderRadius: "6px",
            border: `1px solid ${palette.white[12]}`,
            color: palette.text.disabled,
            fontSize: "11px",
            fontFamily: "Gilroy-SemiBold",
            flexShrink: 0,
          }}
        >
          ESC
        </DSBox>
      </DSBox>
      <DSBox
        ref={listRef}
        id="command-palette-listbox"
        role="listbox"
        sx={{ maxHeight: "50vh", overflowY: "auto", p: 1 }}
      >
        {results.length === 0 && (
          <DSTypography
            variant="body2"
            sx={{ p: 3, textAlign: "center", color: palette.text.disabled }}
          >
            No matches
          </DSTypography>
        )}
        {results.map((item, i) => {
          const showSection = item.section !== lastSection;
          lastSection = item.section;
          return (
            <DSBox key={item.to}>
              {showSection && (
                <DSTypography
                  variant="subtitle2"
                  sx={{
                    px: 2,
                    pt: 1.5,
                    pb: 0.5,
                    fontSize: "11px",
                    textTransform: "uppercase",
                    letterSpacing: "0.12em",
                    color: palette.text.disabled,
                  }}
                >
                  {item.section}
                </DSTypography>
              )}
              <DSBox
                data-index={i}
                id={`command-palette-option-${i}`}
                role="option"
                aria-selected={i === highlighted}
                onClick={() => go(item)}
                onMouseEnter={() => setHighlighted(i)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  borderRadius: "12px",
                  cursor: "pointer",
                  backgroundColor:
                    i === highlighted ? palette.white[8] : "transparent",
                  color:
                    i === highlighted
                      ? palette.text.primary
                      : palette.text.secondary,
                }}
              >
                <DSBox sx={{ display: "flex", color: "inherit" }}>
                  {item.icon}
                </DSBox>
                <DSTypography
                  variant="body2"
                  sx={{ color: "inherit", flex: 1 }}
                >
                  {item.name}
                </DSTypography>
                {i === highlighted && (
                  <KeyboardReturnIcon
                    fontSize="small"
                    sx={{ color: palette.text.disabled }}
                  />
                )}
              </DSBox>
            </DSBox>
          );
        })}
      </DSBox>
    </Dialog>
  );
};

export default CommandPalette;
